import React, { useState } from "react";
import { formatDistance } from "date-fns";
import { Device } from "types/device.types";
import { TemporaryAccessRequest } from "types/temporaryAccess.types";
import {
  DevicesGrid,
  DeviceCard,
  DisabledDeviceCard,
  PendingTemporaryAccessCard,
  PendingTemporaryAccessIcon,
  PendingTemporaryAccessMessage,
  DeviceName,
  DeviceInfo,
  InfoLabel,
  InfoValue,
  StatusBadge,
  LinkButton,
  StatusMessage,
  WifiIcon,
  DeviceNameContainer,
  AlarmIndicator,
  DeleteLinkButton,
  EditButton,
} from "styles/devices/devicesList.styles";
import { DeviceSensorGrid } from "./DeviceSensorGrid";
import { useAuth } from "context/AuthContext";
import { EditAliasModal } from "components/device/EditAliasModal";
import { SkeletonCard } from "components/shared/SkeletonCard";
import { notify } from "utils/notify";
import { useCancelPendingTemporaryAccessRequest } from "hooks/useTemporaryDealerAccess";
import { useUpdateDeviceLinkAlias } from "hooks/useDevices";
import DeviceConfirmationDialog from "components/device/DeviceConfirmationDialog";
import { buildDeviceDetailsPath } from "utils/deviceNavigation";

interface DevicesCardViewProps {
  devices: Device[];
  isLoading?: boolean;
  isAdminView?: boolean;
  pendingTemporaryAccessRequests?: TemporaryAccessRequest[];
  onDeleteClick?: (device: Device) => void;
  onAliasUpdated?: (linkId: number, newAlias: string) => void;
}

const formatTemporaryAccessDate = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

const DevicesCardView: React.FC<DevicesCardViewProps> = ({
  devices,
  isLoading = false,
  isAdminView = false,
  pendingTemporaryAccessRequests = [],
  onDeleteClick,
  onAliasUpdated,
}) => {
  const { user } = useAuth();
  const isAdmin = isAdminView || user?.role === "ADMIN";
  const isDealer = !isAdmin && user?.role === "DEALER";
  const updateAliasMP = useUpdateDeviceLinkAlias();
  const cancelPendingRequestMP = useCancelPendingTemporaryAccessRequest();
  const pendingRequests = pendingTemporaryAccessRequests.filter(
    (request) => request.status === "pending",
  );

  const [editAliasModal, setEditAliasModal] = useState<{
    visible: boolean;
    device: Device | null;
  }>({
    visible: false,
    device: null,
  });
  const [pendingRequestToCancel, setPendingRequestToCancel] =
    useState<TemporaryAccessRequest | null>(null);

  const confirmPendingRequestCancellation = async () => {
    if (!pendingRequestToCancel) return;

    try {
      await cancelPendingRequestMP.mutateAsync(pendingRequestToCancel.id);
      notify.success("Temporary access request cancelled.");
      setPendingRequestToCancel(null);
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        "Unable to cancel the temporary access request.";
      notify.error(message);
    }
  };

  const formatUpdatedDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return formatDistance(date, new Date(), { addSuffix: true });
    } catch (error) {
      return "Unknown";
    }
  };

  const handleEditAlias = (device: Device) => {
    if (device.accessType === "temporary" || typeof device.linkId !== "number") {
      return;
    }
    setEditAliasModal({
      visible: true,
      device,
    });
  };

  const handleCloseAliasModal = () => {
    setEditAliasModal({
      visible: false,
      device: null,
    });
  };

  const handleAliasUpdated = async (newAlias: string) => {
    if (!editAliasModal.device) return;
    const linkId = editAliasModal.device.linkId;
    if (
      editAliasModal.device.accessType === "temporary" ||
      typeof linkId !== "number"
    ) {
      return;
    }

    try {
      await updateAliasMP.mutateAsync({
        linkId,
        linkAlias: newAlias,
      });

      if (onAliasUpdated) {
        onAliasUpdated(linkId, newAlias);
      }

      notify.success("Device alias updated successfully!");
      handleCloseAliasModal();
    } catch (error) {
      console.error("Error updating alias:", error);
      notify.error("Failed to update device alias. Please try again.");
    }
  };

  const isDeviceRestricted = (status: string) => {
    return status === "pending" || status === "rejected";
  };

  const getStatusMessage = (status: string) => {
    if (status === "pending") {
      return {
        text: "This device is waiting for approval. Details will be available after approval.",
        color: "#fff3cd",
      };
    } else if (status === "rejected") {
      return {
        text: "This device has been rejected. Contact support for more information.",
        color: "#f8d7da",
      };
    }
    return null;
  };

  if (isLoading) {
    return (
      <DevicesGrid role="status" aria-busy="true" aria-label="Loading devices">
        {Array.from({ length: 6 }, (_, index) => (
          <SkeletonCard key={index} lines={5} />
        ))}
      </DevicesGrid>
    );
  }

  return (
    <DevicesGrid>
      {pendingRequests.map((request) => (
        <PendingTemporaryAccessCard key={`temporary-access-${request.id}`}>
          <DeviceNameContainer>
            <div style={{ display: "flex", alignItems: "center" }}>
              <PendingTemporaryAccessIcon
                className="pi pi-clock"
                aria-hidden="true"
              />
              <DeviceName>{request.serialNumber}</DeviceName>
            </div>
          </DeviceNameContainer>

          <DeviceInfo>
            <InfoLabel>Status:</InfoLabel>
            <StatusBadge status="pending">Pending approval</StatusBadge>
          </DeviceInfo>

          <DeviceInfo $column>
            <InfoLabel>Temporary access:</InfoLabel>
            <InfoValue>Awaiting customer approval</InfoValue>
          </DeviceInfo>

          <DeviceInfo $column>
            <InfoLabel>Request expires:</InfoLabel>
            <InfoValue>
              {formatTemporaryAccessDate(request.requestExpiresAt)}
            </InfoValue>
          </DeviceInfo>

          <PendingTemporaryAccessMessage>
            This device will appear here with temporary access after the customer
            approves the request.
          </PendingTemporaryAccessMessage>

          {isDealer && (
            <DeleteLinkButton
              type="button"
              onClick={() => setPendingRequestToCancel(request)}
              disabled={cancelPendingRequestMP.isPending}
            >
              {cancelPendingRequestMP.isPending ? "Cancelling..." : "Cancel request"}
            </DeleteLinkButton>
          )}
        </PendingTemporaryAccessCard>
      ))}

      {devices.map((device) => {
        const restricted = isDeviceRestricted(device.linkStatus);
        const statusMessage = restricted
          ? getStatusMessage(device.linkStatus)
          : null;

        const CardComponent = restricted ? DisabledDeviceCard : DeviceCard;
        const shadowData = device.shadowData || {};
        const alarmValue = shadowData.alarms ?? 0;
        const hasError = alarmValue > 255;
        const hasWarning = alarmValue >= 1 && alarmValue <= 255;
        const hasAlarm = hasError || hasWarning;

        const alarmSeverity = (() => {
          if (alarmValue > 255) {
            return "Error";
          } else if (alarmValue >= 1 && alarmValue <= 255) {
            return "Warning";
          } else {
            return "No alarms";
          }
        })();

        return (
          <CardComponent
            key={device.id}
            $hasAlarm={!restricted && hasAlarm}
            alarmSeverity={alarmSeverity}
            $temporary={device.accessType === "temporary"}
          >
            <DeviceNameContainer>
              <div style={{ display: "flex", alignItems: "center" }}>
                <WifiIcon
                  connected={!!device.iotConnectionStatus?.connected}
                  className="pi pi-wifi"
                  aria-label={
                    device.iotConnectionStatus?.connected
                      ? "Connected"
                      : "Disconnected"
                  }
                />
                <DeviceName>{device.serialNumber}</DeviceName>
              </div>
              {/* Only show an indicator when there is something to flag */}
              {!restricted && hasAlarm && (
                <AlarmIndicator
                  $hasAlarm={hasAlarm}
                  alarmSeverity={alarmSeverity}
                  className={
                    hasError
                      ? "pi pi-exclamation-circle"
                      : "pi pi-exclamation-triangle"
                  }
                  title={
                    hasError ? "Device has errors" : "Device has warnings"
                  }
                />
              )}
            </DeviceNameContainer>

            {user?.role !== "ADMIN" && (
              <DeviceInfo>
                <InfoLabel>Status:</InfoLabel>
                <StatusBadge
                  status={
                    device.accessType === "temporary"
                      ? "temporary"
                      : device.linkStatus
                  }
                >
                  {device.accessType === "temporary"
                    ? "temporary access"
                    : device.linkStatus}
                </StatusBadge>
              </DeviceInfo>
            )}

            {device.accessType === "temporary" && (
              <DeviceInfo $column>
                <InfoLabel>Temporary access ends:</InfoLabel>
                <InfoValue>
                  {device.temporaryAccessExpiresAt
                    ? formatTemporaryAccessDate(
                        String(device.temporaryAccessExpiresAt),
                      )
                    : "—"}
                </InfoValue>
              </DeviceInfo>
            )}

            {!restricted ? (
              <>
                {(device.linkStatus === "accepted" || user?.role === "ADMIN") &&
                  shadowData && <DeviceSensorGrid shadowData={shadowData} />}

                {device.location && (
                  <DeviceInfo>
                    <InfoLabel>Location:</InfoLabel>
                    <InfoValue>{device.location}</InfoValue>
                  </DeviceInfo>
                )}

                {user?.role !== "ADMIN" && device.accessType !== "temporary" && (
                  <DeviceInfo $column>
                    <InfoLabel>Alias:</InfoLabel>
                    <InfoValue
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      {device.linkAlias || "No alias set"}
                      <EditButton
                        onClick={() => handleEditAlias(device)}
                        title="Edit alias"
                      >
                        <i className="pi pi-pencil" />
                      </EditButton>
                    </InfoValue>
                  </DeviceInfo>
                )}

                {device.DeviceLocation?.address && (
                  <DeviceInfo $column>
                    <InfoLabel>Address:</InfoLabel>
                    <InfoValue>{device.DeviceLocation.address}</InfoValue>
                  </DeviceInfo>
                )}

                <DeviceInfo>
                  <InfoLabel>Last Updated:</InfoLabel>
                  <InfoValue>{formatUpdatedDate(device.updatedAt)}</InfoValue>
                </DeviceInfo>

                <LinkButton
                  to={buildDeviceDetailsPath({
                    serialNumber: device.serialNumber,
                    linkId: device.linkId,
                    isAdminView: isAdmin,
                  })}
                >
                  View Details
                </LinkButton>
              </>
            ) : (
              <>
                <StatusMessage color={statusMessage?.color}>
                  {statusMessage?.text}
                </StatusMessage>

                <div style={{ flex: 1 }} />

                {onDeleteClick && (
                  <DeleteLinkButton onClick={() => onDeleteClick(device)}>
                    Remove Access
                  </DeleteLinkButton>
                )}
              </>
            )}
          </CardComponent>
        );
      })}

      {editAliasModal.device &&
        editAliasModal.device.accessType !== "temporary" &&
        typeof editAliasModal.device.linkId === "number" &&
        user?.role !== "ADMIN" && (
          <EditAliasModal
            visible={editAliasModal.visible}
            linkId={Number(editAliasModal.device.linkId)}
            currentAlias={editAliasModal.device.linkAlias || ""}
            serialNumber={editAliasModal.device.serialNumber}
            onHide={handleCloseAliasModal}
            onAliasUpdated={handleAliasUpdated}
            isUpdating={updateAliasMP.isPending}
          />
        )}

      <DeviceConfirmationDialog
        visible={!!pendingRequestToCancel}
        title="Cancel temporary access request"
        message={`Cancel the temporary access request for ${pendingRequestToCancel?.serialNumber || "this Lifeguard"}? The customer will no longer be able to approve it.`}
        icon={{
          className: "pi pi-exclamation-triangle",
          color: "var(--red-500)",
        }}
        onHide={() => setPendingRequestToCancel(null)}
        onConfirm={confirmPendingRequestCancellation}
        isProcessing={cancelPendingRequestMP.isPending}
        confirmButtonLabel="Cancel request"
        cancelButtonLabel="Keep request"
        confirmButtonClassName="p-button-danger"
      />
    </DevicesGrid>
  );
};

export default DevicesCardView;
